/**
 * Single-select ObjectRecord picker.
 *
 * Shared across MDM admin (ObjectType field editing) and Records domain
 * (RecordType OBJECT_REFERENCE field filling). Provides a searchable
 * typeahead dropdown that lists ObjectRecords from a target ObjectType.
 *
 * When a value is selected, renders a chip with a clear button.
 * When disabled with a selected value, renders a clickable link that
 * navigates to the record's detail page (hub page for tagged ObjectTypes,
 * MDM admin page otherwise).
 */

import {useState, useEffect, useRef, useCallback, useId} from 'react';
import {useNavigate} from 'react-router-dom';
import {ExternalLink, X} from 'lucide-react';
import {Input} from '@/matic/components/ui/input';
import {useObjectRecord} from '@/matic/features/master-data/hooks/useObjectRecord';
import {useObjectType} from '@/matic/features/master-data/hooks/useObjectType';
import {useObjectRecordPicker} from './use-object-record-picker';
import {getObjectRecordRoute} from './hub-routes';
import {formatReferenceLabel} from './format-reference-label';
import type {PickerOption, ObjectReferencePickerProps} from './types';

type ObjectReferenceFieldProps = ObjectReferencePickerProps & {
  value: string | null;
  onChange: (value: string | null) => void;
};

export function ObjectReferenceField({
  referenceObjectTypeId,
  value,
  onChange,
  error,
  disabled,
  label,
  description,
  required,
  linkTab,
}: ObjectReferenceFieldProps) {
  const id = useId();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const initValueRef = useRef<string | null | undefined>(undefined);

  // Fetch ObjectType metadata for hub-aware routing
  const {data: objectType} = useObjectType(referenceObjectTypeId || undefined);

  const {options, isLoading} = useObjectRecordPicker(
    referenceObjectTypeId,
    searchTerm
  );

  // Fallback fetch for externally-injected values (e.g. Scribe's
  // field_write SSE event) where the value hasn't appeared in the picker's
  // search results. Without this, the chip would render the bare UUID until
  // the user typed a matching search.
  const {data: selectedRecord} = useObjectRecord(
    referenceObjectTypeId,
    value ?? undefined
  );

  // Resolve the selected value's display label
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  // Populate label when an existing value is loaded (e.g. opening edit form
  // or Scribe write). Prefers a hit in current picker results; falls back to
  // the dedicated by-ID fetch above.
  useEffect(() => {
    if (initValueRef.current === value) return;
    if (!value) {
      initValueRef.current = value;
      setSelectedLabel(null);
      return;
    }

    const found = options.find((o) => o.id === value);
    if (found) {
      initValueRef.current = value;
      setSelectedLabel(
        formatReferenceLabel({code: found.code, display: found.detail})
      );
      return;
    }

    if (selectedRecord?.id === value) {
      initValueRef.current = value;
      setSelectedLabel(formatReferenceLabel(selectedRecord));
    }
  }, [value, options, selectedRecord]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setIsOpen(false);
  }, []);

  const handleSelect = (option: PickerOption) => {
    onChange(option.id);
    setSelectedLabel(
      formatReferenceLabel({code: option.code, display: option.detail})
    );
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSelectedLabel(null);
    setSearchTerm('');
    // Focus the search input after clearing
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleNavigateToRecord = () => {
    if (!value) return;
    const route = getObjectRecordRoute(
      objectType?.moduleTag,
      referenceObjectTypeId,
      value,
      linkTab ? {tab: linkTab} : undefined
    );
    navigate(route);
  };

  // Read-only mode: render a clickable link instead of the picker input
  if (disabled && value && selectedLabel) {
    return (
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
        <button
          type="button"
          onClick={handleNavigateToRecord}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800 hover:underline"
        >
          {selectedLabel}
          <ExternalLink className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  const hasSelection = value && selectedLabel;

  return (
    <div ref={dropdownRef} className="relative">
      <label
        htmlFor={id}
        className="mb-1 block text-sm font-medium text-gray-700"
      >
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>

      {hasSelection ? (
        <div
          className={`flex min-h-[38px] items-center rounded-md border px-3 py-1.5 ${
            error ? 'border-red-500' : 'border-neutral-300 bg-white'
          }`}
        >
          <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-0.5 text-sm font-medium text-primary-700">
            {selectedLabel}
            <button
              type="button"
              onClick={handleClear}
              className="ml-0.5 rounded-full p-0.5 hover:bg-primary-100"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        </div>
      ) : (
        <>
          <Input
            ref={inputRef}
            id={id}
            type="text"
            autoComplete="off"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => {
              setIsOpen(true);
            }}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder="Search records..."
            className={error ? 'border-red-500' : ''}
          />

          {/* Dropdown */}
          {isOpen && (
            <div className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
              {isLoading ? (
                <div className="px-3 py-2 text-sm text-gray-400">
                  Loading...
                </div>
              ) : options.length > 0 ? (
                options.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                    onClick={() => {
                      handleSelect(opt);
                    }}
                  >
                    <span className="text-sm">
                      <span className="font-mono text-xs">{opt.code}</span>
                      {opt.detail && (
                        <span className="text-xs text-gray-500">
                          {' — '}
                          {opt.detail}
                        </span>
                      )}
                    </span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-sm text-gray-400">
                  No results found
                </div>
              )}
            </div>
          )}
        </>
      )}

      {error ? (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      ) : (
        description && (
          <p className="mt-1 text-sm text-neutral-500">{description}</p>
        )
      )}
    </div>
  );
}
